import { HttpParams } from '@angular/common/http';
import { toUploadedFile, type UploadedFile } from './uploaded-file';

export interface UploadFontInstance {
  postScriptName: string;
  fontFamily: string;
  fullName?: string;
  fontStyle: string;
  fontWeight: string;
}

export type UploadMethod = 'POST' | 'PUT';

export interface UploadWireRequest {
  readonly url: string;
  readonly method: UploadMethod;
  readonly body: FormData;
  readonly params?: HttpParams;
}

export interface UploadLane<M, R> {
  readonly id: string;
  /** Only an idempotent lane may be re-sent after a lost answer; the engine makes one attempt otherwise. */
  readonly idempotent: boolean;
  wire(file: File, meta: M): UploadWireRequest;
  /** Maps a 2xx body to the lane result; null = the server answered but stored nothing. */
  result(body: unknown): R | null;
}

/** The gateway acks a rejected file with 200 and an empty array, and never raises events for it. */
export function isEmptyUploadAnswer(body: unknown): boolean {
  return Array.isArray(body) && body.length === 0;
}

export type UploadProcess =
  | 'asset'
  | 'font'
  | 'psd-template'
  | 'import-archive'
  | 'spread-preview-medium'
  | 'spread-preview-thumb'
  | 'spread-preview-large';

export interface PhotoLaneMeta {
  readonly projectId: string;
  readonly fileId: string;
  readonly fileTags?: readonly string[];
  readonly uploadBucket?: string;
  readonly rewrite?: boolean;
  readonly originalId?: string;
  readonly forceNew?: boolean;
  readonly reuseDuplicate?: boolean;
  readonly notGenerateNewPersons?: boolean;
  readonly groupFolder?: boolean;
  readonly uploadBatchId?: string;
  readonly sharpening?: string;
}

export interface FileLaneMeta {
  readonly projectId: string;
  readonly fileId: string;
  readonly fileTags?: readonly string[];
  readonly uploadProcess: UploadProcess;
  readonly assetRole?: string;
  readonly fileModifiedAt?: string;
  readonly [extra: string]: unknown;
}

export const PHOTO_LANE: UploadLane<PhotoLaneMeta, UploadedFile> = {
  id: 'photo',
  idempotent: true,
  wire(file, meta) {
    // The uploader reads multipart fields in order: processMetadata has to precede the file.
    const body = new FormData();
    body.append('processMetadata', JSON.stringify(meta));
    body.append('file', file, file.name);
    return { url: '/api/upload/images', method: 'POST', body };
  },
  result: toUploadedFile,
};

export const FILE_LANE: UploadLane<FileLaneMeta, UploadedFile> = {
  id: 'file',
  idempotent: true,
  wire(file, meta) {
    const body = new FormData();
    body.append('file', file, file.name);
    const params = new HttpParams().set('processMetadata', JSON.stringify({ ...meta, fileTags: meta.fileTags ?? [] }));
    return { url: '/api/upload/files', method: 'POST', body, params };
  },
  result: toUploadedFile,
};

export interface CustomUploadLaneSpec<M, R> {
  readonly id: string;
  readonly method?: UploadMethod;
  readonly idempotent?: boolean;
  readonly url: (meta: M) => string;
  readonly form: (file: File, meta: M) => FormData;
  readonly result: (body: unknown) => R | null;
}

export function defineUploadLane<M, R>(spec: CustomUploadLaneSpec<M, R>): UploadLane<M, R> {
  return {
    id: spec.id,
    idempotent: spec.idempotent ?? false,
    wire: (file, meta) => ({ url: spec.url(meta), method: spec.method ?? 'POST', body: spec.form(file, meta) }),
    result: spec.result,
  };
}

export interface FontLaneFields {
  readonly postScriptName: string;
  readonly fontFamily: string;
  readonly fullName?: string;
  readonly fontStyle: string;
  readonly fontWeight: string;
  readonly ownerId?: string | null;
  readonly instances?: readonly UploadFontInstance[];
}

/** The one font metadata shape: ownerId only for an admin reviewer, instances only for a variable font. */
export function fontLaneMeta(projectId: string, fileId: string, fields: FontLaneFields): FileLaneMeta {
  const { ownerId, instances, ...names } = fields;
  return {
    projectId,
    fileId,
    uploadProcess: 'font',
    ...names,
    ...(ownerId ? { ownerId } : {}),
    ...(instances && instances.length > 1 ? { instances } : {}),
  };
}
